import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  LinearProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Receipt as EntryIcon, Search as SearchIcon } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import SortableTableCell from './SortableTableCell';
import JournalEntryRow from './JournalEntryRow';
import EmptyState from './EmptyState';

const JournalEntriesPanel = ({
  filteredJournalEntries,
  selectedJournalEntries,
  transactionTypeFilter,
  journalSortBy,
  journalSortOrder,
  onJournalEntryToggle,
  onSelectAllJournalEntries,
  areAllJournalEntriesSelected,
  onSortChange,
  isLoadingJournalEntries,
  journalEntrySearch,
  onJournalEntrySearchChange,
  reconciliationFilter, // Add filter indicator
  onUnreconcile, // Add unreconcile function
  onEdit, // Add edit function
  onDelete, // Add delete function
}) => {
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let date;
      if (typeof dateString === 'string' && !dateString.includes('T')) {
        const [year, month, day] = dateString.split('-').map((part) => Number(part));
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }

      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const toggleSort = (sortBy) => {
    onSortChange(sortBy);
  };

  return (
    <Box
      sx={{
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: 1.5,
            background: 'linear-gradient(135deg, #9c27b0 0%, #673ab7 100%)',
            color: 'white',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Left side - Title and Count */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <EntryIcon fontSize="small" />
              <Typography variant="subtitle2" fontWeight="600">
                Lançamentos do Vita
              </Typography>
              <Chip
                label={filteredJournalEntries.length}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: alpha(theme.palette.common.white, 0.25),
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  minWidth: 28,
                  '& .MuiChip-label': {
                    px: 0.75,
                  },
                }}
              />
            </Stack>

            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar lançamentos"
                value={journalEntrySearch}
                onChange={(e) => onJournalEntrySearchChange(e.target.value)}
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                  minHeight: 36,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '0.8rem',
                    height: 36,
                  },
                }}
              />
            </Box>

            {/* Right side - Filter and Selection indicators */}
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Filter indicator */}
              {reconciliationFilter === 'unreconciled' && (
                <Chip
                  label="Apenas não conciliados"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.warning.main, 0.9),
                    color: 'white',
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                />
              )}
              {reconciliationFilter === 'reconciled' && (
                <Chip
                  label="Apenas conciliados"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.success.main, 0.9),
                    color: 'white',
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                />
              )}
              {reconciliationFilter === 'all' && (
                <Chip
                  label="Todos os lançamentos"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.info.main, 0.9),
                    color: 'white',
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                />
              )}

              {selectedJournalEntries.length > 0 && (
                <Chip
                  label={`${selectedJournalEntries.length} selecionados`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.25),
                    color: 'white',
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    border: '1px solid rgba(255,255,255,0.3)',
                  }}
                />
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Table Container with proper scrolling */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxHeight: '60vh', // Limit maximum height for better UX
            minHeight: 0,
          }}
        >
          {isLoadingJournalEntries ? (
            <LinearProgress sx={{ flexShrink: 0 }} />
          ) : (
            <TableContainer
              component={Box}
              sx={{
                height: '100%',
                overflow: 'auto',
                minHeight: 0,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#a1a1a1',
                  },
                },
                '&::-webkit-scrollbar-thumb:active': {
                  background: '#919191',
                },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ height: 48 }}>
                    <TableCell
                      padding="checkbox"
                      sx={{
                        backgroundColor: '#f8fafc',
                        height: 48,
                        padding: '8px 12px',
                        borderBottom: '2px solid #e0e0e0',
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={areAllJournalEntriesSelected}
                        indeterminate={
                          selectedJournalEntries.length > 0 &&
                          selectedJournalEntries.length < filteredJournalEntries.length
                        }
                        onChange={(e) => onSelectAllJournalEntries(e.target.checked)}
                      />
                    </TableCell>
                    <SortableTableCell
                      label="Data"
                      sortBy="date"
                      currentSortBy={journalSortBy}
                      currentSortOrder={journalSortOrder}
                      onSort={toggleSort}
                      tooltip="Ordenar por data (nova/antiga)"
                      sx={{ minWidth: 90 }}
                    />
                    <SortableTableCell
                      label="Descrição"
                      sortBy="description"
                      currentSortBy={journalSortBy}
                      currentSortOrder={journalSortOrder}
                      onSort={toggleSort}
                      tooltip="Ordenar por descrição (A-Z/Z-A)"
                      sx={{ minWidth: 200 }}
                    />
                    <SortableTableCell
                      label="Valor"
                      sortBy="value"
                      currentSortBy={journalSortBy}
                      currentSortOrder={journalSortOrder}
                      onSort={toggleSort}
                      tooltip="Ordenar por valor (maior/menor)"
                      align="right"
                      sx={{ minWidth: 100 }}
                    />
                    <SortableTableCell
                      label="Status"
                      sortBy="status"
                      currentSortBy={journalSortBy}
                      currentSortOrder={journalSortOrder}
                      onSort={toggleSort}
                      tooltip="Ordenar por status (conciliado/pendente)"
                      align="center"
                      sx={{ minWidth: 80 }}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredJournalEntries.length > 0 ? (
                    filteredJournalEntries.map((entry) => (
                      <JournalEntryRow
                        key={entry.id}
                        entry={entry}
                        isSelected={selectedJournalEntries.some((e) => e.id === entry.id)}
                        onToggle={onJournalEntryToggle}
                        formatDate={formatDate}
                        onUnreconcile={onUnreconcile}
                        onEdit={onEdit}
                        onDelete={onDelete}
                      />
                    ))
                  ) : (
                    <EmptyState
                      type="journalEntries"
                      isLoading={isLoadingJournalEntries}
                      searchTerm={journalEntrySearch}
                      filterType={transactionTypeFilter}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default JournalEntriesPanel;
